import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
  IconographyThemes,
  Button,
  Pagination,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  getCategoriesSagaAction,
  getRequirementsSagaAction,
  getTestCaseSagaAction,
  createRequirementSagaAction,
  updateRequirementSagaAction,
  deleteRequirementSagaAction,
  extractNumericId,
} from "../../../../../../redux/saga/project-validation/saga-actions";
import { getTechnologiesSagaAction } from "../../../../../../redux/saga/technology-settings/technologies/saga-actions";
import { projectValidationSliceAction } from "../../../../../../redux/slices/project-validation";
import { RequirementType } from "../../../../../../redux/slices/project-validation/Types";
import AddRequirementModal from "./AddRequirementModal";
import DeleteModal from "../../../../../../common/components/DeleteModal";
import partnerValidationImage from "../../../../../../common/assets/images/partner-validation.png";
import "./RequirementManagement.scss";

const isRequirementRef = (
  value: unknown
): value is { id?: unknown; name?: unknown } =>
  typeof value === "object" && value !== null;

/** Prefer display name; fall back to id for legacy string/number values. */
const normalizeRequirementValue = (value: unknown): string => {
  if (value == null) return "";
  if (isRequirementRef(value)) {
    if (value.name != null && String(value.name).trim()) {
      return String(value.name).trim();
    }
    if (value.id != null && String(value.id).trim()) {
      return String(value.id).trim();
    }
    return "";
  }
  return String(value).trim();
};

const normalizeRequirementArray = (values: unknown): string[] => {
  if (!Array.isArray(values)) return [];
  return values.map(normalizeRequirementValue).filter(Boolean);
};

/** Resolve technology filter keys (settings IDs) from API refs or legacy values. */
const getRequirementRefId = (value: unknown): string | null => {
  if (isRequirementRef(value) && value.id != null) {
    const id = String(value.id).trim();
    return id || null;
  }
  return null;
};

const RequirementManagement: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const { categories, requirements, testCases, selectedTestCase, status } =
    useAppSelector((state) => state.project_validation);
  const { technologies } = useAppSelector(
    (state) => state.technology_settings_technologies
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewModalType, setViewModalType] = useState<
    "testcase" | "description"
  >("testcase");
  const [selectedRequirement, setSelectedRequirement] =
    useState<RequirementType | null>(null);
  const [editingRequirement, setEditingRequirement] =
    useState<RequirementType | null>(null);
  const [deletingRequirementId, setDeletingRequirementId] = useState<
    number | null
  >(null);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedIntegrationTypes, setSelectedIntegrationTypes] = useState<
    string[]
  >([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>(
    []
  );
  const catsScrollRef = React.useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    dispatch(getCategoriesSagaAction());
    dispatch(getRequirementsSagaAction());
    dispatch(getTechnologiesSagaAction());
  }, [dispatch]);

  const activeTechnologies = useMemo(
    () => technologies.filter((technology) => technology.status === "Active"),
    [technologies]
  );

  const technologyFilterOptions = useMemo(
    () =>
      activeTechnologies
        .map((technology) => ({
          label: technology.name,
          value: String(technology.id),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [activeTechnologies]
  );

  const getRequirementTechnologyKeys = useMemo(() => {
    return (reqTechnology: unknown): string[] => {
      if (!Array.isArray(reqTechnology)) return [];
      return reqTechnology
        .map((tech) => {
          const refId = getRequirementRefId(tech);
          if (refId) {
            return refId;
          }

          const normalizedRaw = normalizeRequirementValue(tech);
          const normalized =
            normalizedRaw === "Identity Positioning" ? "OIP" : normalizedRaw;
          if (!normalized) return null;

          const byId = activeTechnologies.find(
            (technology) => String(technology.id) === normalized
          );
          if (byId) {
            return String(byId.id);
          }

          const byName = activeTechnologies.find(
            (technology) => technology.name === normalized
          );
          if (byName) {
            return String(byName.id);
          }

          return normalized;
        })
        .filter((value): value is string => Boolean(value));
    };
  }, [activeTechnologies]);

  const getTechnologyDisplayName = useMemo(() => {
    return (value: unknown): string => {
      if (isRequirementRef(value) && value.name != null && String(value.name).trim()) {
        const name = String(value.name).trim();
        return name === "Identity Positioning" ? "OIP" : name;
      }

      const normalized = normalizeRequirementValue(value);
      const legacyName = normalized === "Identity Positioning" ? "OIP" : normalized;
      const byId = activeTechnologies.find(
        (technology) => String(technology.id) === legacyName
      );
      if (byId) {
        return byId.name;
      }

      const byName = activeTechnologies.find(
        (technology) => technology.name === legacyName
      );
      return byName?.name || legacyName;
    };
  }, [activeTechnologies]);

  const matchesSelectedTechnologies = (
    reqTechnology: unknown,
    selectedTechnologyIds: string[]
  ) => {
    if (selectedTechnologyIds.length === 0) {
      return true;
    }

    const requirementTechnologyKeys = getRequirementTechnologyKeys(reqTechnology);
    return selectedTechnologyIds.some((technologyId) =>
      requirementTechnologyKeys.includes(technologyId)
    );
  };

  // Filter requirements by technology and integration type (excluding category filter for counts)
  const requirementsForCounts = useMemo(() => {
    let filtered = requirements;

    // Filter by technologies
    if (selectedTechnologies.length > 0) {
      filtered = filtered.filter((req) =>
        matchesSelectedTechnologies(req.technology, selectedTechnologies)
      );
    }

    // Filter by integration types
    if (selectedIntegrationTypes.length > 0) {
      filtered = filtered.filter((req) => {
        if (!req.integration_type) return false;
        const reqInts = normalizeRequirementArray(req.integration_type);
        const transformedInts = reqInts;
        return selectedIntegrationTypes.some((int) =>
          transformedInts.includes(int)
        );
      });
    }

    return filtered;
  }, [requirements, selectedTechnologies, selectedIntegrationTypes, activeTechnologies]);

  // Group requirements by category and calculate counts based on filtered requirements
  const categoriesWithCounts = useMemo(() => {
    const categoryMap = new Map();
    categories.forEach((cat) => {
      categoryMap.set(cat.ID, {
        ...cat,
        requirementCount: requirementsForCounts.filter(
          (req) => req.category_id === cat.ID
        ).length,
      });
    });
    return Array.from(categoryMap.values())
      .filter((cat) => cat.requirementCount > 0)
      .sort((a, b) => b.requirementCount - a.requirementCount);
  }, [categories, requirementsForCounts]);

  // useEffect(() => {
  //   if (categoriesWithCounts.length > 0 && selectedCategory === null) {
  //     setSelectedCategory(categoriesWithCounts[0].ID);
  //   }
  // }, [categoriesWithCounts, selectedCategory]);

  const uniqueIntegrationTypes = useMemo(() => {
    const intSet = new Set<string>();
    requirements.forEach((req) => {
      normalizeRequirementArray(req.integration_type).forEach((integrationType) => {
        intSet.add(integrationType);
      });
    });
    return Array.from(intSet).sort();
  }, [requirements]);

  // Filter requirements by category and filters
  const filteredRequirements = useMemo(() => {
    let filtered = requirements;

    // Filter by category
    if (selectedCategories.length > 0) {
  filtered = filtered.filter((req) =>
    selectedCategories.includes(req.category_id)
  );
}

    // Filter by technologies
    if (selectedTechnologies.length > 0) {
      filtered = filtered.filter((req) =>
        matchesSelectedTechnologies(req.technology, selectedTechnologies)
      );
    }

    // Filter by integration types
    if (selectedIntegrationTypes.length > 0) {
      filtered = filtered.filter((req) => {
        if (!req.integration_type) return false;
        const reqInts = normalizeRequirementArray(req.integration_type);
        const transformedInts = reqInts;
        return selectedIntegrationTypes.some((int) =>
          transformedInts.includes(int)
        );
      });
    }

    return filtered;
  }, [
    requirements,
    selectedCategories,
    selectedTechnologies,
    selectedIntegrationTypes,
    activeTechnologies,
  ]);

  // Pagination logic
  const totalRecords = filteredRequirements.length;
  const startIndex = (currentPageNumber - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRequirements = filteredRequirements.slice(
    startIndex,
    endIndex
  );

  const handlePageChange = (page: number) => {
    setCurrentPageNumber(page);
  };

  const onPageSizesClick = (_e: React.MouseEvent<HTMLElement>, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  // Reset pagination when category changes
useEffect(() => {
  setCurrentPageNumber(1);
}, [selectedCategories]);

  // Check scroll position
  React.useEffect(() => {
    const checkScroll = () => {
      const el = catsScrollRef.current;
      if (!el) return;
      setAtStart(el.scrollLeft <= 0);
      setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth);
    };

    const el = catsScrollRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      checkScroll(); // Initial check
      return () => el.removeEventListener("scroll", checkScroll);
    }
  }, [categoriesWithCounts]);

  const handleOpenModal = () => {
    setEditingRequirement(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRequirement(null);
  };

  const handleEditRequirement = (requirement: RequirementType) => {
    setEditingRequirement(requirement);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (requirementId: number) => {
    setDeletingRequirementId(requirementId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deletingRequirementId) {
      dispatch(deleteRequirementSagaAction(deletingRequirementId));
    }
    setIsDeleteModalOpen(false);
    setDeletingRequirementId(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletingRequirementId(null);
  };

  const handleSaveRequirement = (requirementData: {
    category: string;
    requirementType: string;
    technology: Array<string | number>;
    integrationType: Array<string | number>;
    testCases: string[];
    validationScore: string;
    status: boolean;
    highLevelScenario: string;
    requirementDescription: string;
  }) => {
    // Arrays are sent directly to backend as IDs
    const technology = (requirementData.technology || []).map((value) =>
      Number(value)
    );
    const integrationType = (requirementData.integrationType || []).map(
      (value) => Number(value)
    );
    const testCases = requirementData.testCases || [];

    if (editingRequirement) {
      dispatch(
        updateRequirementSagaAction({
          id: editingRequirement.ID,
          category_id: Number(requirementData.category),
          requirement_type: requirementData.requirementType,
          technology: technology,
          integration_type: integrationType,
          test_cases: testCases,
          validation_score: requirementData.validationScore,
          requirement_status: requirementData.status ? "Active" : "Inactive",
          high_level_scenario: requirementData.highLevelScenario || null,
          requirement_description:
            requirementData.requirementDescription || null,
        })
      );
    } else {
      dispatch(
        createRequirementSagaAction({
          category_id: Number(requirementData.category),
          requirement_type: requirementData.requirementType,
          technology: technology,
          integration_type: integrationType,
          test_cases: testCases,
          validation_score: requirementData.validationScore,
          requirement_status: requirementData.status ? "Active" : "Inactive",
          high_level_scenario: requirementData.highLevelScenario || null,
          requirement_description:
            requirementData.requirementDescription || null,
        })
      );
    }
    // Modal will show success state, then close itself
  };

  const handleViewClick = (
    requirement: RequirementType,
    type: "testcase" | "description",
    testcase?: string
  ) => {
    setSelectedRequirement(requirement);
    setViewModalType(type);
    setIsViewModalOpen(true);

    // If viewing test case, find it in Redux slice or fetch from API
    if (type === "testcase" && testcase) {
      // Extract numeric ID if it's in formatted form (TC-01 -> 01)
      const numericId = extractNumericId(testcase);

      // Try to find the test case in the Redux slice first
      // Test case IDs in slice might be formatted (TC-01) or numeric
      const foundTestCase = testCases.find((tc) => {
        const tcId = extractNumericId(tc.ID);
        return tcId === numericId || tc.ID === testcase || tc.ID === numericId;
      });

      if (foundTestCase) {
        // Test case found in slice, set it directly without API call
        dispatch(
          projectValidationSliceAction.setSelectedTestCase(foundTestCase)
        );
      } else {
        // Test case not found in slice, fetch from API
        dispatch(getTestCaseSagaAction(numericId));
      }
    } else if (type === "testcase" && requirement.test_cases) {
      // Fallback: if no testcase parameter but requirement has test_cases
      const testCaseIds = requirement.test_cases;
      if (testCaseIds.length > 0) {
        const testCaseId = testCaseIds[0];
        // Extract numeric ID if it's in formatted form (TC-01 -> 01)
        const numericId = extractNumericId(testCaseId);

        // Try to find in slice first
        const foundTestCase = testCases.find((tc) => {
          const tcId =
            typeof tc.ID === "string" ? extractNumericId(tc.ID) : String(tc.ID);
          return (
            tcId === numericId || tc.ID === testCaseId || tc.ID === numericId
          );
        });

        if (foundTestCase) {
          dispatch(
            projectValidationSliceAction.setSelectedTestCase(foundTestCase)
          );
        } else {
          dispatch(getTestCaseSagaAction(numericId));
        }
      }
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedRequirement(null);
  };

  const scrollByAmount = (px: number) => {
    const el = catsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: px, behavior: "smooth" });
  };

  // map array to multiple span tags
  const mapArrayToSpan = (array: Array<string | number>) => {
    return array.map((item) => <span key={String(item)}>{String(item)}</span>);
  };

  const mapArrayToButton = (array: string[], req: any) => {
    return array.map((item) => (
      <button
        key={item}
        type="button"
        className="test-case-link"
        onClick={() => {
          handleViewClick(req, "testcase", item);
        }}
      >
        {`TC-${item}`}
      </button>
    ));
  };
  return (
    <div className="requirement-management-content">
      {status.categoriesLoading || status.requirementsLoading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "50px" }}
        >
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
        <>
          {categoriesWithCounts.length === 0 ? (
            /* Empty State */
            <>
              <div className="empty-state-header">
                <Typography className="empty-state-text">
                  No requirements have been created yet. Please create
                  categories and test cases before adding requirements.
                </Typography>
              </div>
              <div className="empty-state-button-container">
                <Button
                  variant="primary"
                  label="Add requirement"
                  icon={<Iconography icon="plus" />}
                  onClick={handleOpenModal}
                  disabled={readOnly}
                />
              </div>
              <div className="empty-state">
                <img src={partnerValidationImage} alt="No requirements" />
              </div>
            </>
          ) : (
            <>
              {/* Header with Add button */}
              <div className="rm-header">
                <Typography variant="h6" className="rm-title">
                  Requirements
                </Typography>
                <Button
                  variant="primary"
                  label="Add requirement"
                  icon={<Iconography icon="plus" />}
                  onClick={handleOpenModal}
                  disabled={readOnly}
                />
              </div>

              {/* Filters */}
              <div className="filters-row">
                <FormControl className="filter-select">
                  <label className="filter-label">Select technology</label>
                  <Select
                    multiple
                    value={selectedTechnologies}
                    onChange={(e) =>
                      setSelectedTechnologies(e.target.value as string[])
                    }
                    input={<OutlinedInput />}
                    displayEmpty
                    size="small"
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <em style={{ color: "#9CA3AF" }}>Select</em>;
                      }
                      return selected
                        .map((value) => {
                          const option = technologyFilterOptions.find(
                            (technologyOption) =>
                              technologyOption.value === value
                          );
                          return option?.label || value;
                        })
                        .join(", ");
                    }}
                  >
                    {technologyFilterOptions.map((technologyOption) => (
                      <MenuItem
                        key={technologyOption.value}
                        value={technologyOption.value}
                      >
                        <Checkbox
                          checked={
                            selectedTechnologies.indexOf(
                              technologyOption.value
                            ) > -1
                          }
                        />
                        <ListItemText primary={technologyOption.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl className="filter-select">
                  <label className="filter-label">
                    Select Integration Type
                  </label>
                  <Select
                    multiple
                    value={selectedIntegrationTypes}
                    onChange={(e) => {
                      console.log(e.target.value);
                      setSelectedIntegrationTypes(e.target.value as string[]);
                    }}
                    input={<OutlinedInput />}
                    displayEmpty
                    size="small"
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <em style={{ color: "#9CA3AF" }}>Select</em>;
                      }
                      return selected.join(", ");
                    }}
                  >
                    {uniqueIntegrationTypes.map((intType) => (
                      <MenuItem key={intType} value={intType}>
                        <Checkbox
                          checked={
                            selectedIntegrationTypes.indexOf(intType) > -1
                          }
                        />
                        <ListItemText primary={intType} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl className="filter-select">
  <label className="filter-label">
    Categories
  </label>

  <Select
    multiple
    value={selectedCategories}
    onChange={(e) =>
      setSelectedCategories(e.target.value as number[])
    }
    input={<OutlinedInput />}
    displayEmpty
    size="small"
    renderValue={(selected) => {
      const values = selected as number[];

      if (values.length === 0) {
        return <em style={{ color: "#9CA3AF" }}>Select</em>;
      }

      return categoriesWithCounts
        .filter((category) =>
          values.includes(category.ID)
        )
        .map((category) => category.category_name)
        .join(", ");
    }}
  >
    {categoriesWithCounts.map((category) => (
      <MenuItem
        key={category.ID}
        value={category.ID}
      >
        <Checkbox
          checked={selectedCategories.includes(category.ID)}
        />
        <ListItemText
          primary={`${category.category_name} (${category.requirementCount})`}
        />
      </MenuItem>
    ))}
  </Select>
</FormControl>


              </div>

              {/* Requirements Section */}
              <div className="requirements-section">

                {/* Categories Scroll Container */}
                {/*
                      <section className="gdl-categories-row" aria-label="Categories">
                        <Button
                          appearance="outlined"
                          disabled={atStart}
                          className="gdl-cat-scroll-btn gdl-cat-scroll-btn--prev"
                          onClick={() => scrollByAmount(-320)}
                          aria-label="Scroll categories left"
                          label={
                            <Iconography
                              icon="angleLeft"
                              size={IconographySizes.Small}
                            />
                          }
                        />

                        <div
                          ref={catsScrollRef}
                          className="gdl-cat-scroll"
                          role="listbox"
                        >
                          {categoriesWithCounts.map((category) => (
                            <button
                              key={category.ID}
                              className={`gdl-cat-pill ${
                                selectedCategory === category.ID
                                  ? "gdl-cat-pill--selected"
                                  : ""
                              }`}
                              onClick={() => setSelectedCategory(category.ID)}
                              aria-pressed={selectedCategory === category.ID}
                            >
                              <div className="gdl-cat__content">
                                <span className="gdl-cat-pill__title">
                                  {category.category_name}
                                </span>
                                <span className="gdl-cat-pill__count">
                                  {category.requirementCount} Requirements
                                </span>
                              </div>

                              {selectedCategory === category.ID ? (
                                <Iconography
                                  icon="circleCheck"
                                  size={IconographySizes.Small}
                                  className="gdl-cat-pill__icon"
                                />
                              ) : (
                                <div className="gdl-cat-pill__radio" />
                              )}
                            </button>
                          ))}
                        </div>

                        <Button
                          appearance="outlined"
                          disabled={atEnd}
                          className="gdl-cat-scroll-btn gdl-cat-scroll-btn--next"
                          onClick={() => scrollByAmount(320)}
                          aria-label="Scroll categories right"
                          label={
                            <Iconography
                              icon="angleRight"
                              size={IconographySizes.Small}
                            />
                          }
                        />
                      </section>
                      */}

                {/* Requirements Table */}
                <TableContainer
                  component={Paper}
                  className="requirements-table"
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Requirement ID</TableCell>
                        <TableCell>High Level Scenario</TableCell>
                        <TableCell>Technology</TableCell>
                        <TableCell>Req. Type</TableCell>
                        <TableCell>Integration Type</TableCell>
                        <TableCell>Test case</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRequirements.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No requirements found
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedRequirements.map((req) => (
                          <TableRow key={req.ID}>
                            <TableCell>{req.requirement_ID}</TableCell>
                            <TableCell>{req.high_level_scenario}</TableCell>
                            <TableCell>
                              <div className="array-column">
                                {Array.isArray(req.technology) &&
                                  mapArrayToSpan(
                                    req.technology.map((tech) =>
                                      getTechnologyDisplayName(tech)
                                    )
                                  )}
                              </div>
                            </TableCell>
                            <TableCell>{req.requirement_type}</TableCell>
                            <TableCell>
                              <div className="array-column">
                                {Array.isArray(req.integration_type) &&
                                  mapArrayToSpan(
                                    normalizeRequirementArray(req.integration_type)
                                  )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="array-column">
                                {Array.isArray(req.test_cases) &&
                                  mapArrayToButton(req.test_cases, req)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <button
                                type="button"
                                className="view-link"
                                onClick={() => {
                                  handleViewClick(req, "description");
                                }}
                              >
                                View
                              </button>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  req.requirement_status === "Active" ||
                                  req.requirement_status === "active"
                                    ? "Active"
                                    : "Inactive"
                                }
                                className={`status-chip ${(
                                  req.requirement_status || ""
                                ).toLowerCase()}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="flat"
                                icon={<Iconography icon="pen" />}
                                onClick={() => handleEditRequirement(req)}
                                disabled={readOnly}
                              />
                              <Button
                                variant="flat"
                                icon={<Iconography icon="trashCan" />}
                                onClick={() => handleDeleteClick(req.ID)}
                                className="delete-button"
                                disabled={readOnly}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalRecords > 0 && (
                  <div
                    className="hid-pagination-container"
                    style={{ marginTop: "20px" }}
                  >
                    <Pagination
                      currentPageNumber={currentPageNumber}
                      totalRecords={totalRecords}
                      pageSize={pageSize}
                      onPageNumberClick={handlePageChange}
                      onPageSizesClick={onPageSizesClick}
                      pageSizes={[
                        { label: "10", value: "10" },
                        { label: "20", value: "20" },
                        { label: "50", value: "50" },
                      ]}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
      <AddRequirementModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveRequirement}
        editData={editingRequirement}
      />

      {isDeleteModalOpen && (
        <DeleteModal
          setDeleteOpen={setIsDeleteModalOpen}
          onDeleteButtonClick={handleDeleteConfirm}
          deleteButtonLoading={status.deleteRequirementLoading}
          optionalHeader="Delete Requirement"
          message="Are you sure you want to delete this requirement? This action cannot be undone."
        />
      )}
      <Dialog
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 500, fontSize: "20px" }}>
            {viewModalType === "testcase" ? "Test Case" : "Description"}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseViewModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <Iconography icon="xmark" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewModalType === "testcase" && selectedTestCase ? (
            <>
              {status.testCaseLoading ? (
                <Box
                  className="test-case-details-modal"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "24px",
                  }}
                >
                  <Loader variant={LoaderSizes.large} />
                </Box>
              ) : (
                <Box className="test-case-details-modal">
                  <div className="detail-item">
                    <Typography className="detail-label">
                      Test Case ID
                    </Typography>
                    <Typography className="detail-value">
                      {selectedTestCase.test_case_id}
                    </Typography>
                  </div>
                  <div className="detail-item">
                    <Typography className="detail-label">
                      High Level Scenario
                    </Typography>
                    <Typography className="detail-content">
                      {selectedTestCase.high_level_scenario || "N/A"}
                    </Typography>
                  </div>
                  <div className="detail-item">
                    <Typography className="detail-label">
                      Precondition
                    </Typography>
                    <Typography className="detail-content">
                      {selectedTestCase.pre_conditions || "N/A"}
                    </Typography>
                  </div>
                  <div className="detail-item">
                    <Typography className="detail-label">
                      Description
                    </Typography>
                    <Typography className="detail-content">
                      {selectedTestCase.description || "N/A"}
                    </Typography>
                  </div>
                  <div className="detail-item">
                    <Typography className="detail-label">
                      Expected Result
                    </Typography>
                    <Typography className="detail-content">
                      {selectedTestCase.expected_result || "N/A"}
                    </Typography>
                  </div>
                  <div className="detail-item">
                    <Typography className="detail-label">Status</Typography>
                    <Chip
                      label={
                        selectedTestCase.test_case_status === "Active" ||
                        selectedTestCase.test_case_status === "active"
                          ? "Active"
                          : "Inactive"
                      }
                      className={`status-chip ${(
                        selectedTestCase.test_case_status || ""
                      ).toLowerCase()}`}
                    />
                  </div>
                </Box>
              )}
            </>
          ) : selectedRequirement ? (
            <Box className="description-modal">
              <Typography className="description-content">
                {selectedRequirement.requirement_description || "N/A"}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequirementManagement;
